import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material';
import {
  availableThemeModes,
  cycleThemeMode,
  getTheme,
  type ThemeMode,
  themeModeLabels,
  useThemeMode,
} from '@ops-matrix/design-system-ui';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { env } from '../config/env';
import { useBusinessBranding } from '../lib/trpc/hooks';

interface TenantTheme {
  primaryColor?: string;
  secondaryColor?: string;
  logo?: string;
  companyName?: string;
  /** Custom label for the consumer portal (e.g., "Borrower Portal") */
  consumerPortalLabel?: string;
}

interface ThemeContextValue {
  tenantTheme: TenantTheme | null;
  setTenantTheme: (theme: TenantTheme | null) => void;
  portalType: 'admin' | 'business' | 'consumer' | null;
  tenantId: string | null;
  redirectUrl: string | null;
  /** Lead ID for form pre-fill during registration */
  leadId: string | null;
  /** Business ID for lead lookup during registration */
  businessId: string | null;
  /** Set business ID dynamically (e.g., from invite API response) to trigger branding fetch */
  setBusinessId: (id: string | null) => void;
  mode: ThemeMode;
  setMode: (mode: ThemeMode | 'system') => void;
  toggleMode: () => void;
  /** Cycle through all 4 themes: light -> dark -> twilight -> midnight -> light */
  cycleMode: () => void;
  /** All available theme modes */
  availableModes: ThemeMode[];
  /** Get display label for a theme mode */
  getModeLabel: (mode: ThemeMode) => string;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const PLATFORM_DOMAINS = ['localhost', '127.0.0.1', 'opsmatrix.dev', 'fiops.ai'];

// Parse URL params once (synchronously)
function getUrlParams() {
  if (typeof window === 'undefined') {
    return { portalType: null, tenantId: null, redirectUrl: null, leadId: null, businessId: null };
  }
  const params = new URLSearchParams(window.location.search);
  // Accept both 'redirect' (common convention) and 'returnUrl' (legacy)
  const redirectUrl = params.get('redirect') || params.get('returnUrl');

  // Try to get leadId, tenantId, and businessId from top-level params first
  // businessId can be 'b' (short) or 'businessId' (full) in the invite URL
  let leadId = params.get('leadId');
  let tenantId = params.get('tenant');
  let businessId = params.get('businessId') || params.get('b');

  // If not found as top-level params, try to extract from redirect URL
  // This handles cases where Consumer Portal embeds them in the redirect
  if (redirectUrl && (!leadId || !businessId || !tenantId)) {
    try {
      const redirectUrlObj = new URL(redirectUrl);
      const redirectParams = redirectUrlObj.searchParams;
      if (!leadId) leadId = redirectParams.get('leadId');
      if (!tenantId) tenantId = redirectParams.get('tenant');
      if (!businessId) businessId = redirectParams.get('businessId') || redirectParams.get('b');
    } catch {
      // Invalid URL, ignore
    }
  }

  return {
    portalType: params.get('portal') as 'admin' | 'business' | 'consumer' | null,
    tenantId,
    redirectUrl,
    leadId,
    businessId,
  };
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Theme Provider for Auth Portal
 *
 * Uses the unified design system while supporting:
 * - Portal type detection (admin, business, consumer)
 * - Tenant-specific theming (logo, colors)
 * - All 4 themes: Light, Dark, Twilight (blue-tinted), Midnight (near-black)
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  // Parse URL params synchronously to avoid effect-based setState
  const initialParams = useMemo(() => getUrlParams(), []);

  const [tenantTheme, setTenantTheme] = useState<TenantTheme | null>(null);
  const [portalType] = useState<'admin' | 'business' | 'consumer' | null>(initialParams.portalType);
  const [tenantId] = useState<string | null>(initialParams.tenantId);
  const [redirectUrl] = useState<string | null>(initialParams.redirectUrl);
  const [leadId] = useState<string | null>(initialParams.leadId);
  const [businessId, setBusinessId] = useState<string | null>(initialParams.businessId);

  // Resolve businessId from custom domain redirect URL when not explicitly provided
  // e.g., redirect=https://app.coastal-funding.com/ → resolve coastal-funding.com → businessId
  useEffect(() => {
    if (businessId || !redirectUrl) return;
    try {
      const parsed = new URL(redirectUrl);
      // Skip platform domains — they don't need resolution
      const isPlatform = PLATFORM_DOMAINS.some((d) => parsed.hostname === d || parsed.hostname.endsWith(`.${d}`));
      if (isPlatform) return;

      // Only resolve portal.* and app.* subdomains of custom domains
      if (!parsed.hostname.startsWith('portal.') && !parsed.hostname.startsWith('app.')) return;
      const baseDomain = parsed.hostname.replace(/^(portal|app)\./, '');
      if (!baseDomain.includes('.')) return;

      fetch(`${env.VITE_API_URL}/api/v1/website/resolve-domain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hostname: baseDomain }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data: { businessId?: string } | null) => {
          if (data?.businessId) setBusinessId(data.businessId);
        })
        .catch(() => {
          // Resolution failed — fall back to default branding
        });
    } catch {
      // Invalid redirect URL — ignore
    }
  }, [businessId, redirectUrl, setBusinessId]);

  // Fetch business branding when businessId is present (for white-label auth pages)
  const { data: brandingData } = useBusinessBranding(businessId);

  // Update tenant theme when branding data is fetched
  useEffect(() => {
    if (brandingData) {
      setTenantTheme({
        companyName: brandingData.name || undefined,
        logo: brandingData.logo || undefined,
        primaryColor: brandingData.primaryColor || undefined,
        secondaryColor: brandingData.secondaryColor || undefined,
        consumerPortalLabel: (brandingData as Record<string, unknown>).consumerPortalLabel as string | undefined,
      });
    }
  }, [brandingData]);

  // Update browser tab title when company name changes
  useEffect(() => {
    if (tenantTheme?.companyName) {
      document.title = `${tenantTheme.companyName} - Sign In`;
    } else {
      document.title = 'Sign In';
    }
  }, [tenantTheme?.companyName]);

  // Use the design system's theme mode hook
  // Use default 'theme-mode' key so theme persists across all portals
  const { mode, setMode, toggle } = useThemeMode({
    defaultMode: 'system',
    storageKey: 'theme-mode',
  });

  // Cycle through all themes
  const cycleMode = useCallback(() => {
    const nextMode = cycleThemeMode(mode);
    setMode(nextMode);
  }, [mode, setMode]);

  // Get label for a mode
  const getModeLabel = useCallback((themeMode: ThemeMode) => themeModeLabels[themeMode] || themeMode, []);

  // Get base theme from design system and optionally extend with tenant colors
  const muiTheme = useMemo(() => {
    // Use getTheme to support all theme modes (light, dark, twilight, midnight)
    const baseTheme = getTheme(mode);

    // If no tenant theme, use the design system theme as-is
    if (!tenantTheme) {
      return baseTheme;
    }

    // Merge tenant theme colors with design system theme
    return {
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        primary: {
          ...baseTheme.palette.primary,
          main: tenantTheme.primaryColor ?? baseTheme.palette.primary.main,
        },
        secondary: {
          ...baseTheme.palette.secondary,
          main: tenantTheme.secondaryColor ?? baseTheme.palette.secondary.main,
        },
      },
    };
  }, [mode, tenantTheme]);

  const value = useMemo(
    () => ({
      tenantTheme,
      setTenantTheme,
      portalType,
      tenantId,
      redirectUrl,
      leadId,
      businessId,
      setBusinessId,
      mode,
      setMode,
      toggleMode: toggle,
      cycleMode,
      availableModes: availableThemeModes,
      getModeLabel,
    }),
    [
      tenantTheme,
      portalType,
      tenantId,
      redirectUrl,
      leadId,
      businessId,
      setBusinessId,
      mode,
      setMode,
      toggle,
      cycleMode,
      getModeLabel,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
